import { computed, ref } from 'vue'

type FsDocument = Document & {
  webkitFullscreenEnabled?: boolean
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

const active = ref(false)
const apiOk = ref(false)
const pwaKiosk = ref(false)
let listening = false

function fsEnabled(): boolean {
  const doc = document as FsDocument
  return !!(document.fullscreenEnabled || doc.webkitFullscreenEnabled)
}

function fsElement(): Element | null {
  const doc = document as FsDocument
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

function isPwaFullscreen(): boolean {
  return window.matchMedia('(display-mode: fullscreen)').matches
}

function sync(): void {
  active.value = !!fsElement()
  pwaKiosk.value = isPwaFullscreen() && !fsElement()
}

/**
 * Browser Fullscreen API (not F11). Shared across the top bar, Settings,
 * and the duel rail so they all show the same on/off state.
 */
export function listenDesktopFullscreen(): void {
  if (listening || typeof document === 'undefined') return
  listening = true
  apiOk.value = fsEnabled()
  sync()
  document.addEventListener('fullscreenchange', sync)
  document.addEventListener('webkitfullscreenchange', sync)
  window.matchMedia('(display-mode: fullscreen)').addEventListener(
    'change',
    sync,
  )
}

export function useDesktopFullscreen() {
  listenDesktopFullscreen()

  const available = computed(() => apiOk.value && !pwaKiosk.value)

  async function enter(): Promise<void> {
    const el = document.documentElement as FsElement
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen({ navigationUI: 'hide' })
        return
      }
      await el.webkitRequestFullscreen?.()
    } catch {
      /* permission / not a user gesture */
    }
  }

  async function exit(): Promise<void> {
    const doc = document as FsDocument
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
        return
      }
      await doc.webkitExitFullscreen?.()
    } catch {
      /* already left */
    }
  }

  async function toggle(): Promise<void> {
    if (fsElement()) await exit()
    else await enter()
  }

  return { active, available, toggle }
}
